import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { AmountCode,
    StockDisplayType,
    ReportsView} from 'typings/enums';
import { ChangeHistoryEmployeeCycle } from 'nomst/Stores/ChangeHistory/changeHistoryEmployeeCycle';
import { changeHistoryStore } from 'utils/MobxDataManager/singletons';
import {
    PercentageCalculationRule
} from 'nomst/Stores/Configuration/Constants/percentageCalculationRule';
import { AmountPayType } from 'nomst/Stores/Configuration/Constants/amountPayType';
import { Amount } from 'nomst/Stores/Decision/amount';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { IAmount } from 'typings/amount';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { formatTimestamp } from 'utils/dateUtils';
import SafeMath from 'common/safeMath';

export interface IChangeHistoryEmployeeCycleViewProps {
    configuration: Configuration;
    navigationStore: NavigationStore;
    timestamp: string;
    changeHistoryEmployeeCycle: ChangeHistoryEmployeeCycle;
    selectedCurrency: Nullable<string>;
}
interface IChangeHistoryEmployeeCycleViewState {
    showDetails: boolean;
}

export class ChangeHistoryEmployeeCycleView
    extends React.Component<IChangeHistoryEmployeeCycleViewProps, IChangeHistoryEmployeeCycleViewState> {
    constructor(props: IChangeHistoryEmployeeCycleViewProps) {
        super(props);
        this.handleClickToggleDetailView = this.handleClickToggleDetailView.bind(this);
        this.handleClickEmployeeDetails = this.handleClickEmployeeDetails.bind(this);
        this.renderAmountChange = this.renderAmountChange.bind(this);
        this.state = {
            showDetails: false
        };
    }

    render(): React.ReactNode {
        const { changeHistoryEmployeeCycle, timestamp, selectedCurrency } = this.props;
        if (!changeHistoryEmployeeCycle.stateAfterChange
            || !selectedCurrency) {
            return null;
        }

        return (
            <div className={this.getHistoryItemClassName}>
                <div className="initials-border">
                    <div className="initials-sm">{changeHistoryEmployeeCycle.modifyingUser.displayInitial}</div>
                    <div className="dot-border" />
                </div>
                <div className="history-item-content">
                    <div className="history-top">
                        <span>{formatTimestamp(timestamp)}</span>
                        <button
                            className="no-btn-style history-item__toggle"
                            onClick={this.handleClickToggleDetailView}
                        >
                            {this.getViewDetailsButtonText}
                            <span className={this.getViewDetailsButtonClassName}/>
                        </button>
                    </div>
                    <strong className="display-name">
                        {changeHistoryEmployeeCycle.modifyingUser.displayName}
                    </strong> made changes for&#32;
                    <button
                        className="pxl-link"
                        onClick={this.handleClickEmployeeDetails}
                    >
                        {changeHistoryEmployeeCycle.employee.displayName}
                    </button>
                    <div className="row flex-row">
                        {this.renderAmounts()}
                        {this.renderJobTitleChange()}
                    </div>
                    <div className="history-details">
                        <div className="history-details__column history-details__column--current">
                            <h4 className="history-details__heading">Current Pay</h4>
                            <strong data-mf-replace-inner="***">
                                {this.getDisplayText(
                                    changeHistoryEmployeeCycle.employeeCurrentPay(),
                                    changeHistoryEmployeeCycle.employee.isHourly)}
                            </strong>
                        </div>
                        <div className="history-details__column history-details__column--new">
                            <h4 className="history-details__heading">New Pay</h4>
                            <del data-mf-replace-inner="***">
                                {this.getDisplayText(
                                    changeHistoryEmployeeCycle.newPay(changeHistoryEmployeeCycle.stateBeforeChange),
                                    changeHistoryEmployeeCycle.employee.isHourly)}
                            </del>
                            <strong data-mf-replace-inner="***">
                                {this.getDisplayText(
                                    changeHistoryEmployeeCycle.newPay(changeHistoryEmployeeCycle.stateAfterChange),
                                    changeHistoryEmployeeCycle.employee.isHourly)}
                            </strong>
                        </div>
                        <div className="history-details__column history-details__column--tcc">
                            <h4 className="history-details__heading">TCC</h4>
                            <del data-mf-replace-inner="***">
                                {this.getDisplayText(
                                    changeHistoryEmployeeCycle
                                        .tcc(changeHistoryEmployeeCycle.stateBeforeChange),
                                    changeHistoryEmployeeCycle.employee.isHourly,
                                    false)}
                            </del>
                            <strong data-mf-replace-inner="***">
                                {this.getDisplayText(
                                    changeHistoryEmployeeCycle
                                        .tcc(changeHistoryEmployeeCycle.stateAfterChange),
                                    changeHistoryEmployeeCycle.employee.isHourly,
                                    false)}
                            </strong>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    handleClickToggleDetailView(): void {
        const { showDetails } = this.state;
        this.setState({
            showDetails: !showDetails
        });
    }

    handleClickEmployeeDetails(): void {
        const { navigationStore, configuration } = this.props;
        const { changeHistoryEmployeeCycle } = this.props;

        navigationStore.navigateToEmployee(
            configuration.cycleId,
            changeHistoryStore.manager.employeeSourceId,
            null,
            changeHistoryEmployeeCycle?.employee.employeeSourceId,
            ReportsView.DirectsOnly);
    }

    renderAmounts(): React.ReactNode {
        const { configuration } = this.props;
        return configuration.cycleBudgetFields
            .map(this.renderAmountChange);
    }

    getStockAmountChangeText(
        cycleBudgetField: CycleBudgetField,
        stockAmount: Amount,
        isHourly: boolean): string {
        let amountText = '';
        if (cycleBudgetField.stockDisplayType === StockDisplayType.Unit) {
            amountText = `${stockAmount.value}`;
        } else {
            amountText = this.getDisplayText(stockAmount.value, isHourly, false);
            const unitValueText = this.getUnitValue(stockAmount.value, cycleBudgetField.stockValue);
            if (unitValueText !== '') {
                amountText += ` / ${unitValueText}`;
            }
        }
        return amountText;
    }

    getAmountChangeText(
        cycleBudgetField: CycleBudgetField,
        updatedAmount: Amount,
        isHourly: boolean): string {
        const annualOnly = cycleBudgetField.payType !== AmountPayType.BasePay;
        let amountText = this.getDisplayText(updatedAmount.value, isHourly, !annualOnly);
        let amountPercentageText = '';

        if (cycleBudgetField.percentageCalculationRule === PercentageCalculationRule.PercentageOfTarget) {
            amountPercentageText = this.getTargetPercentage(updatedAmount.value, updatedAmount.targetAmount);
        } else {
            amountPercentageText = this.getPercentageText(
                updatedAmount.amountForChangeHistoryDisplay,
                annualOnly);
        }

        if (amountPercentageText) {
            amountText += ` (${amountPercentageText})`;
        }
        return amountText;
    }

    getProrationText(
        updatedAmount: Amount,
        annualOnly: boolean): string {
        let prorationPercentageText = '';
        if (updatedAmount.isProrated && !updatedAmount.isProrationOverride) {
            prorationPercentageText = this.getPercentageText(
                updatedAmount.amountForChangeHistoryDisplay,
                annualOnly,
                updatedAmount.prorateRatio);
        }
        return prorationPercentageText;
    }

    renderAmountChange(cycleBudgetField: CycleBudgetField): React.ReactNode {
        const { stateBeforeChange, stateAfterChange, employee } = this.props.changeHistoryEmployeeCycle;
        let amountBefore = stateBeforeChange
            .amounts
            .find(amount => amount.amountCode === cycleBudgetField.amountCode);
        const amountAfter = stateAfterChange
            .amounts
            .find(amount => amount.amountCode === cycleBudgetField.amountCode);

        if (!amountAfter) {
            return null;
        }

        if (!amountBefore) {
            amountBefore = new Amount(
                {
                    amountCode: cycleBudgetField.amountCode,
                    value: 0
                } as IAmount,
                employee.employeeSourceId,
                employee.currentPay,
                null,
                cycleBudgetField.percentageCalculationRule,
                cycleBudgetField.payType,
                null,
                null,
                new CurrencyConversion(this.props.configuration.currencies),
                null);
        }

        if (amountBefore.value === amountAfter.value) {
            return null;
        }

        let beforeText = '';
        let afterText = '';

        if (cycleBudgetField.amountCode === AmountCode.Stock) {
            beforeText = this.getStockAmountChangeText(cycleBudgetField, amountBefore, employee.isHourly);
            afterText = this.getStockAmountChangeText(cycleBudgetField, amountAfter, employee.isHourly);
        } else {
            beforeText = this.getAmountChangeText(cycleBudgetField, amountBefore, employee.isHourly);
            afterText = this.getAmountChangeText(cycleBudgetField, amountAfter, employee.isHourly);
        }

        const prorationPercentageText = this.getProrationText(
            amountAfter,
            cycleBudgetField.payType !== AmountPayType.BasePay);

        return (
            <div className="history-item-content-detail col-sm-6" key={cycleBudgetField.amountCode}>
                <span>{cycleBudgetField.title}</span><br />
                <del data-mf-replace-inner="***">{beforeText}</del>
                <strong data-mf-replace-inner="***">{afterText}</strong>
                {this.renderProration(
                    amountAfter.isProrated && !amountAfter.isProrationOverride,
                    prorationPercentageText)}
            </div>
        );
    }

    renderProration(isProrated: boolean, prorationPercentageText: string): React.ReactNode {
        if (isProrated === false) {
            return null;
        }

        return (
            <div className="pxl-text-xs">
                <span className="pxl-text-c2">Proration <strong>{prorationPercentageText}</strong></span>
            </div>
        );
    }

    renderJobTitleChange(): React.ReactNode {
        const { stateBeforeChange, stateAfterChange } = this.props.changeHistoryEmployeeCycle;

        if (stateBeforeChange.promotionJobTitle === stateAfterChange.promotionJobTitle) {
            return null;
        }

        const promotionJobTitleBefore =
            !stateBeforeChange.promotionJobTitle ? ' ' : stateBeforeChange.promotionJobTitle;

        return (
            <div className="history-item-content-detail col-sm-6">
                <span>Job Title</span><br />
                <del>{promotionJobTitleBefore}</del> <strong>{stateAfterChange.promotionJobTitle}</strong>
            </div>
        );
    }

    get getHistoryItemClassName(): string {
        const { showDetails } = this.state;
        let className = 'history-item flex-row';

        if (showDetails) {
            className += ' show-details';
        }

        return className;
    }

    get getViewDetailsButtonText(): string {
        const { showDetails } = this.state;
        return showDetails ? 'Hide Details ' : 'View Details ';
    }

    get getViewDetailsButtonClassName(): string {
        const { showDetails } = this.state;
        return showDetails ? 'icon-sort-up' : 'icon-sort-down';
    }

    getPercentageText(
        value: number,
        annualOnly: boolean = false,
        prorateRatio: Nullable<number> = null): string {
        const { configuration, changeHistoryEmployeeCycle } = this.props;
        let percentageText = '';
        if (prorateRatio && changeHistoryEmployeeCycle) {
            const prorationPercentage = changeHistoryEmployeeCycle.getAmountPercentage(
                value, annualOnly) * prorateRatio;
            if (prorationPercentage >= 0) {
                percentageText = configuration.format.formatDecimalToPercentage(prorationPercentage);
            }
        } else {
            const percentage = changeHistoryEmployeeCycle?.getAmountPercentage(value, annualOnly);
            percentageText = configuration.format.formatDecimalToPercentage(percentage);
        }
        return percentageText;
    }

    getTargetPercentage(value: Nullable<number>, total: Nullable<number>): string {
        const { configuration } = this.props;
        if (!total || isNaN(total)) {
            return '';
        }

        return configuration.format.formatDecimalToPercentage(SafeMath.divide(value || 0, total));
    }

    getDisplayText(value: number, isHourly: boolean = false, showPerHour: boolean = true): string {
        const { configuration, selectedCurrency } = this.props;
        const { currencyConversion, employee } = this.props.changeHistoryEmployeeCycle;
        return currencyConversion.convertCurrencyToTextDisplay(
            employee.isoCurrencyCode,
            configuration.format,
            value,
            configuration.hasMultipleCurrency,
            selectedCurrency ?? '',
            isHourly,
            showPerHour);
    }

    getUnitValue(value: number, conversionValue: Nullable<number>): string {
        if (!conversionValue || conversionValue === 0) {
            return '';
        }
        const { configuration } = this.props;
        const { currencyConversion, employee } = this.props.changeHistoryEmployeeCycle;

        const conversion = currencyConversion.convertCurrency(
            configuration.homeCountryCurrency,
            employee.isoCurrencyCode,
            conversionValue);

        return configuration.format.formatStockDecimal(SafeMath.divide(value, conversion));
    }
}

export default inject('store')(observer(ChangeHistoryEmployeeCycleView));